import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, Send, AlertCircle } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { track } from "../../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ORG_TYPES = ["Bank", "Building society", "Mortgage firm", "Wealth / investment", "Insurance", "Consumer credit", "Payments", "Fintech", "Other"];
const AREAS = ["Customer-facing AI", "Call monitoring / QA", "Employee AI", "AI agents", "Consumer Duty monitoring", "Vulnerable customers", "Sensitive information", "AI governance", "Other"];
const VOLUMES = ["Under 10k/month", "10k–100k", "100k–1m", "1m+", "Not sure"];

const initial = { name: "", company: "", role: "", email: "", org_type: ORG_TYPES[0], area: AREAS[0], volume: VOLUMES[0], notes: "", consent: false };

export default function FSWalkthroughForm() {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const [done, setDone] = useState(false);

  const setField = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (!form.company.trim()) e.company = "Please enter your company.";
    if (!form.role.trim()) e.role = "Please enter your role.";
    if (!form.email.trim()) e.email = "Please enter your business email.";
    else if (!EMAIL_RE.test(form.email)) e.email = "Please enter a valid email address.";
    if (!form.consent) e.consent = "Please confirm you consent to us processing your details.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) { toast.error("Please check the highlighted fields."); return; }
    setSubmitting(true);
    try {
      const payload = {
        full_name: form.name,
        email: form.email,
        company: form.company,
        role: form.role,
        enquiry_type: "Financial Services Walkthrough",
        control_area: form.area,
        monthly_volume: form.volume,
        environment: `Organisation type: ${form.org_type}${form.notes ? `\n\nControl focus: ${form.notes}` : ""}`,
        message: `Financial Services walkthrough — ${form.org_type} — ${form.area}`,
        consent: form.consent,
      };
      await axios.post(`${API}/demo-requests`, payload);
      track("fs_walkthrough_submitted", { org_type: form.org_type, area: form.area });
      navigate(`/thank-you?type=fs`);
    } catch (err) {
      toast.error("Something went wrong. Please try again or email info@norse3.com.");
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="rounded-lg border border-signal-green/30 bg-signal-green/[0.06] p-10 text-center" data-testid="fs-walkthrough-success">
        <CheckCircle2 className="mx-auto h-12 w-12 text-signal-green" />
        <h3 className="mt-5 text-2xl font-bold text-foreground">Request received</h3>
        <p className="mt-3 text-muted-foreground max-w-md mx-auto">Thank you. A member of the norse3 team will be in touch. For anything urgent, email <a href="mailto:info@norse3.com" className="text-primary hover:underline">info@norse3.com</a>.</p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate className="rounded-lg border border-white/10 bg-navy-900/60 p-6 sm:p-8" data-testid="fs-walkthrough-form">
      <div className="grid sm:grid-cols-2 gap-5">
        <F label="Name" required error={errors.name}><Input value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="Jane Doe" className="bg-navy-800 border-white/12" data-testid="fs-name" /></F>
        <F label="Company" required error={errors.company}><Input value={form.company} onChange={(e) => setField("company", e.target.value)} placeholder="Firm Ltd" className="bg-navy-800 border-white/12" data-testid="fs-company" /></F>
        <F label="Role" required error={errors.role}><Input value={form.role} onChange={(e) => setField("role", e.target.value)} placeholder="Head of Compliance" className="bg-navy-800 border-white/12" data-testid="fs-role" /></F>
        <F label="Business email" required error={errors.email}><Input type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} placeholder="jane@firm.co.uk" className="bg-navy-800 border-white/12" data-testid="fs-email" /></F>
        <F label="Organisation type" required>
          <Select value={form.org_type} onValueChange={(v) => setField("org_type", v)}>
            <SelectTrigger className="bg-navy-800 border-white/12" data-testid="fs-org-type"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-navy-800 border-white/12">{ORG_TYPES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
        </F>
        <F label="Area of interest" required>
          <Select value={form.area} onValueChange={(v) => setField("area", v)}>
            <SelectTrigger className="bg-navy-800 border-white/12" data-testid="fs-area"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-navy-800 border-white/12">{AREAS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
        </F>
      </div>

      <div className="mt-5">
        <F label="Approximate interaction volume" required>
          <Select value={form.volume} onValueChange={(v) => setField("volume", v)}>
            <SelectTrigger className="bg-navy-800 border-white/12" data-testid="fs-volume"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-navy-800 border-white/12">{VOLUMES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
        </F>
      </div>

      <div className="mt-5">
        <F label="Tell us what you're trying to control (optional)">
          <Textarea value={form.notes} onChange={(e) => setField("notes", e.target.value)} rows={3} className="bg-navy-800 border-white/12 resize-none" placeholder="Which AI systems, channels or customer journeys you'd like to bring under control." data-testid="fs-notes" />
        </F>
      </div>

      <label className="mt-6 flex items-start gap-3 cursor-pointer">
        <Checkbox checked={form.consent} onCheckedChange={(v) => setField("consent", !!v)} className="mt-0.5 border-white/25" data-testid="fs-consent" />
        <span className="text-sm text-muted-foreground">I consent to norse3 processing my details to respond to this request. <span className="text-signal-red">*</span></span>
      </label>
      {errors.consent && <p className="mt-2 flex items-center gap-1.5 text-sm text-signal-red"><AlertCircle className="h-4 w-4" /> {errors.consent}</p>}

      <button type="submit" disabled={submitting} data-testid="fs-walkthrough-submit" className="mt-7 inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-70">
        {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : <><Send className="h-4 w-4" /> Request walkthrough</>}
      </button>
    </form>
  );
}

function F({ label, required, error, children }) {
  return (
    <div>
      <Label className="text-sm text-foreground">{label} {required && <span className="text-signal-red">*</span>}</Label>
      <div className="mt-1.5">{children}</div>
      {error && <p className="mt-1.5 flex items-center gap-1.5 text-sm text-signal-red"><AlertCircle className="h-4 w-4" /> {error}</p>}
    </div>
  );
}
